import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  automationService,
  type StrategySpecSummary,
  type StrategySpec,
  type StopRule,
  type TrailingStopRule,
  type ScannerFilter,
} from '@/services/strategyService';
import { History } from 'lucide-react';

interface SpecHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ruleId: string;
  ruleName: string;
}

// ── Formatting helpers ─────────────────────────────────────

const fmtDate = (iso: string) => new Date(iso).toLocaleString();

const fmtWindow = (a: string | null, b: string | null) =>
  a || b ? `${a ?? '—'} – ${b ?? '—'}` : '—';

const fmtStop = (s: StopRule | null) => (s ? `${s.mode} ×${s.value}` : 'None');

const fmtTrail = (t: TrailingStopRule | null) =>
  t
    ? `${t.mode} ${t.value}${t.mode === 'Percent' ? '%' : ''} (activate ${t.activationPercent}%)` +
      // distanceReference changes percent-trail behavior (LastPrice vs
      // MaxFavorable) — include it so an edit to it shows in the diff.
      (t.distanceReference ? ` ref ${t.distanceReference}` : '')
    : 'None';

const fmtFilter = (f: ScannerFilter) => {
  const v = JSON.stringify(f.value);
  const v2 = f.value2 !== undefined && f.value2 !== null ? ` / ${JSON.stringify(f.value2)}` : '';
  return `${f.field} ${f.operator} ${v}${v2}`;
};

/** Polymorphic asset config (EquityRuleConfig / ForexRuleConfig). Rendered as
 *  formatted JSON so ANY field change shows in the diff — including
 *  forex-only settings (lot size, pip stops, rollover blackout) that have no
 *  flat-field equivalent. */
const fmtRuleConfig = (rc: unknown | null): string =>
  rc == null ? 'None' : JSON.stringify(rc, null, 1);

/** Flattens a spec into ordered (label, value) rows for the diff table.
 *  Covers every trade-affecting field on StrategySpec — a version that
 *  changed only an omitted field would falsely render as "no change". */
const specRows = (spec: StrategySpec): Array<[string, string]> => [
  ['Scanner', spec.scannerId],
  ['Direction', spec.direction],
  ['Asset Class', spec.assetClass],
  ['Position Sizing', `${spec.sizing.mode} ${spec.sizing.value}`],
  ['Account Equity', `$${spec.sizing.accountEquity}`],
  ['Max Notional', `$${spec.sizing.maxNotional}`],
  ['Stop Loss', fmtStop(spec.exits.stopLoss)],
  ['Take Profit', fmtStop(spec.exits.takeProfit)],
  ['Trailing Stop', fmtTrail(spec.exits.trailing)],
  ['Break-Even After R', String(spec.exits.breakEvenAfterRMultiple)],
  ['Max Hold (min)', String(spec.exits.maxHoldMinutes)],
  ['Session End (ET)', spec.exits.sessionEndTimeEt ?? '—'],
  ['Max Loss %', spec.exits.maxLossPercent != null ? `${spec.exits.maxLossPercent}%` : 'None'],
  ['Cooldown (s)', String(spec.cooldownSeconds)],
  ['Max Concurrent Trades', String(spec.maxConcurrentTrades)],
  ['Entry Fill Timeout (s)', spec.entryFillTimeoutSeconds != null ? `${spec.entryFillTimeoutSeconds}s` : 'Default (60s)'],
  ['Regular Hours Only', spec.regularHoursOnly ? 'Yes' : 'No'],
  ['Entry Window (ET)', fmtWindow(spec.entryStartTimeEt, spec.entryEndTimeEt)],
  ['Lunch Blackout (ET)', fmtWindow(spec.lunchBlackoutStartEt, spec.lunchBlackoutEndEt)],
  ['Asset Config', fmtRuleConfig(spec.ruleConfig)],
  ['ORB Entry Trigger', spec.orbEntry
    ? `margin ${spec.orbEntry.breakoutMarginBps}bps · ${spec.orbEntry.requireCloseBeyond ? 'close beyond' : 'touch'}`
      + (spec.orbEntry.requireCompleteRange ? ' · range complete' : '')
    : 'None (scanner edge)'],
  ['Opening Range (ET)', spec.exits.openRangeEndEt
    // A null end is DERIVED from openRangeMinutes, so rendering only the clock fields makes two
    // versions with different durations look identical.
    ? fmtWindow(spec.exits.openRangeStartEt ?? null, spec.exits.openRangeEndEt)
    : `${spec.exits.openRangeStartEt ?? '09:30:00'} + ${spec.exits.openRangeMinutes || 0} min`
      + (spec.exits.openRangeMinutes ? '' : ' (first bar)')],
  ['Max Entries / Day', spec.maxEntriesPerSymbolPerDay ? String(spec.maxEntriesPerSymbolPerDay) : 'Unlimited'],
  ['Min Stop Distance', spec.minStopDistancePct ? `${spec.minStopDistancePct}%` : 'None'],
  ['Backtest Commission', spec.backtestCommissionPerUnit || spec.backtestCommissionPerTrade
    ? `$${spec.backtestCommissionPerUnit ?? 0}/unit + $${spec.backtestCommissionPerTrade ?? 0}/trade (round trip)`
    : 'None'],
  ['Backtest Exits', spec.backtestIntrabarExits ? 'Intrabar (bar high/low)' : 'Close-only (legacy)'],
  ['Entry Filters', spec.entryFilters.length ? spec.entryFilters.map(fmtFilter).join('\n') : 'None'],
];

// ── Component ──────────────────────────────────────────────

export const SpecHistoryDialog: React.FC<SpecHistoryDialogProps> = ({
  open,
  onOpenChange,
  ruleId,
  ruleName,
}) => {
  const [versions, setVersions] = useState<StrategySpecSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  // Full specs fetched lazily per version. Keyed `${ruleId}:${version}` — a
  // late response from a previously-selected rule must not shadow the active
  // rule's spec under a bare version number.
  const [specCache, setSpecCache] = useState<Record<string, StrategySpec>>({});
  const [specError, setSpecError] = useState<string | null>(null);

  // Load the version list whenever the dialog opens for a rule.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelectedVersion(null);
    setSpecCache({});
    setSpecError(null);
    automationService
      .getSpecVersions(ruleId)
      .then(list => {
        if (cancelled) return;
        setVersions(list);
        // Default to the most recent edit — the usual "what changed last" question.
        if (list.length > 0) setSelectedVersion(list[list.length - 1].version);
      })
      .catch(e => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, ruleId]);

  const fetchSpec = useCallback(
    async (version: number) => {
      const spec = await automationService.getSpecVersion(ruleId, version);
      setSpecCache(prev => ({ ...prev, [`${ruleId}:${version}`]: spec }));
      return spec;
    },
    [ruleId],
  );

  // Ensure the selected version (and its predecessor, for the diff) are cached.
  useEffect(() => {
    if (selectedVersion == null) return;
    let cancelled = false;
    setSpecError(null);
    const wanted = selectedVersion > 1 ? [selectedVersion, selectedVersion - 1] : [selectedVersion];
    Promise.all(wanted.filter(v => !specCache[`${ruleId}:${v}`]).map(fetchSpec)).catch(e => {
      if (!cancelled) setSpecError(e instanceof Error ? e.message : String(e));
    });
    return () => {
      cancelled = true;
    };
    // specCache intentionally omitted — fetchSpec writes it; re-running on its
    // change would loop. The filter(!specCache[…]) reads the latest closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVersion, ruleId, fetchSpec]);

  const current =
    selectedVersion != null ? specCache[`${ruleId}:${selectedVersion}`] : undefined;
  const prior =
    selectedVersion != null && selectedVersion > 1
      ? specCache[`${ruleId}:${selectedVersion - 1}`]
      : undefined;
  const hasPrior = selectedVersion != null && selectedVersion > 1;
  // The detail panel needs the selected spec AND, for a non-v1 version, its
  // predecessor — otherwise `current` can land one render before `prior` and
  // flash an empty panel between the "Loading" message and the diff table.
  const detailReady = !!current && (!hasPrior || !!prior);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" /> Version History — {ruleName}
          </DialogTitle>
          <DialogDescription>
            Every edit appends an immutable spec version. Trades are attributed to the version
            that was active when they opened.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="text-sm text-muted-foreground py-6 text-center">Loading versions…</div>}

        {error && (
          <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">{error}</div>
        )}

        {!loading && !error && versions.length === 0 && (
          <div className="text-sm text-muted-foreground py-6 text-center">
            No version history yet — this rule has had no recorded edits.
          </div>
        )}

        {!loading && !error && versions.length > 0 && (
          <div className="grid grid-cols-[160px_1fr] gap-4 max-h-[60vh]">
            {/* ── Version list ── */}
            <div className="overflow-auto space-y-1 pr-1">
              {[...versions].reverse().map(v => (
                <button
                  key={v.version}
                  onClick={() => setSelectedVersion(v.version)}
                  className={`w-full text-left rounded-md px-2.5 py-2 text-xs transition-colors ${
                    selectedVersion === v.version
                      ? 'bg-primary/10 border border-primary'
                      : 'border border-transparent hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold">v{v.version}</span>
                    {v.version === versions[versions.length - 1].version && (
                      <Badge variant="default" className="text-[9px] px-1 py-0">
                        current
                      </Badge>
                    )}
                  </div>
                  <div className="text-muted-foreground mt-0.5">{fmtDate(v.createdAt)}</div>
                  <div className="text-muted-foreground truncate">
                    {v.createdByUserId ?? 'system'}
                  </div>
                </button>
              ))}
            </div>

            {/* ── Diff / detail panel ── */}
            <div className="overflow-auto">
              {specError && (
                <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                  {specError}
                </div>
              )}

              {!specError && !detailReady && (
                <div className="text-sm text-muted-foreground py-6 text-center">Loading spec…</div>
              )}

              {!specError && detailReady && (
                <>
                  <div className="text-xs text-muted-foreground mb-2">
                    {hasPrior
                      ? `Changes in v${selectedVersion} vs v${selectedVersion! - 1} — changed fields highlighted.`
                      : `v${selectedVersion} — initial version.`}
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 sticky top-0">
                      <tr>
                        <th className="p-2 text-left font-medium">Field</th>
                        {hasPrior && (
                          <th className="p-2 text-left font-medium text-muted-foreground">
                            v{selectedVersion! - 1}
                          </th>
                        )}
                        <th className="p-2 text-left font-medium">v{selectedVersion}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {specRows(current).map(([label, value], i) => {
                        const priorValue = prior ? specRows(prior)[i][1] : undefined;
                        const changed = hasPrior && priorValue !== value;
                        return (
                          <tr
                            key={label}
                            className={`border-b border-muted/20 ${changed ? 'bg-amber-500/10' : ''}`}
                          >
                            <td className="p-2 align-top font-medium">{label}</td>
                            {hasPrior && (
                              <td className="p-2 align-top whitespace-pre-line text-muted-foreground">
                                {priorValue}
                              </td>
                            )}
                            <td
                              className={`p-2 align-top whitespace-pre-line ${
                                changed ? 'text-amber-600 dark:text-amber-400 font-medium' : ''
                              }`}
                            >
                              {value}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
